import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
// Feature Components
import TaskBoard from './components/feature/TaskBoard';
import CreateTaskForm from './components/feature/CreateTaskForm';
// Layout Components
import Header from './components/layout/Header';
import History from './components/layout/History';
// Common Components
import ErrorBoundary from './components/common/ErrorBoundary';
import { ToastContainer } from './components/common/Toast';
// Pages
import Login from './pages/Login';
import Register from './pages/Register';
// Context
import { AuthProvider, useAuth, ToastProvider } from './context';
// Styles
import 'bootstrap/dist/css/bootstrap.min.css';
import './styles/App.css';

// Loading Spinner Component
function LoadingSpinner() {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}
      role="status"
      aria-label="Loading application"
    >
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #e5e7eb',
        borderTop: '4px solid #22c55e',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <span className="visually-hidden">Loading...</span>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

// Public Route (redirect if already authenticated)
function PublicRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  return !isAuthenticated ? children : <Navigate to="/taskBoard" replace />;
}

// Main App Content
function AppContent() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingSpinner />;

  return (
    <>
      {isAuthenticated && <Header />}

      <main role="main" id="main-content">
        <Routes>
          <Route path="/" element={isAuthenticated ? <Navigate to="/taskBoard" /> : <Navigate to="/login" />} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/taskBoard" element={<ProtectedRoute><TaskBoard /></ProtectedRoute>} />
          <Route path="/create" element={<ProtectedRoute><CreateTaskForm /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </main>

      {isAuthenticated && <History />}
      <ToastContainer />
    </>
  );
}

// App with Providers
function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <AppContent />
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;